import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import usePageContent from '@/lib/usePageContent';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const tabs = [
  { id: 'live', label: 'Live Competitions' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
];

const modeLabels = {
  online: 'Online',
  offline: 'Offline',
  hybrid: 'Online + Offline',
};

const statusStyles = {
  live: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  upcoming: 'bg-orange-50 text-[#C45A00] ring-orange-200',
  past: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const artCategories = [
  'Watercolor',
  'Acrylic',
  'Oil',
  'Pencil Sketching',
  'Charcoal',
  'Ink Art',
  'Handmade Canvas',
  'Mixed Media',
  'Digital Painting by Artist',
];

const notAllowedItems = [
  'Fully AI-generated artwork',
  'Copied, traced, stolen, or plagiarized artwork',
  'Adult, hateful, illegal, or misleading artwork',
  'AI images submitted as handmade artwork',
];

const recognitionTiers = [
  ['Top 25', 'Shortlisted Artists'],
  ['Top 10', 'Finalists'],
  ['Top 3', 'Grand Winners'],
  ['5-10', 'Special Mentions'],
];

const proofItems = [
  'Progress images',
  '10-30 second creation video',
  'Timelapse clip',
  'Brushwork or sketching clip',
  'Digital painting process screenshots',
  'Screen recording showing the artwork being created',
];

const defaultSocialCaption = `Proud to participate in the KalaManch Founding Painters Challenge 2026.

Every artist deserves a stage.
Here is my artwork submission for the challenge.

I now nominate 5 fellow artists to showcase their talent on KalaManch.art:
@artist1 @artist2 @artist3 @artist4 @artist5

Join the movement:
https://www.kalamanch.art

#KalaManch #FoundingArtists #IndianArtists #PaintingCompetition #ArtistCommunity #HiddenTalentOfIndia #HarTalentKoManch`;

const foundingPaintersPoster = '/images/competitions/founding-painters-challenge-2026.png';
const foundingPaintersHindiDetails = `थीम: "भारत आपकी नज़रों से"

KalaManch.art द्वारा आयोजित पहला ऑनलाइन पेंटिंग चैलेंज, जिसका उद्देश्य भारत भर के प्रतिभाशाली पेंटर्स और विज़ुअल आर्टिस्ट्स को खोजने, प्रमोट करने और उन्हें मंच देना है।

हर कलाकार भारत को अलग नज़रों से देखता है - रंगों, भावनाओं, संस्कृति, त्योहारों, परंपराओं, गांवों, शहरों, लोगों और कल्पनाओं के माध्यम से।

यह प्रतियोगिता कलाकारों को अपनी कला के माध्यम से भारत को व्यक्त करने का अवसर देती है।

भागीदारी पूरी तरह FREE है।

मान्य कला श्रेणियां:
- वॉटरकलर पेंटिंग
- एक्रिलिक पेंटिंग
- ऑयल पेंटिंग
- पेंसिल स्केचिंग
- चारकोल आर्ट
- इंक आर्ट
- हस्तनिर्मित मिक्स्ड मीडिया
- हैंडमेड कैनवास आर्टवर्क
- कलाकार द्वारा बनाई गई डिजिटल पेंटिंग

मान्य नहीं:
- AI द्वारा पूरी तरह बनाई गई कला
- AI इमेज को हस्तनिर्मित बताना
- कॉपी, ट्रेस, चोरी या प्लेजराइज़्ड आर्टवर्क
- आपत्तिजनक, भ्रामक या गैरकानूनी सामग्री

भागीदारी नियम:
1. प्रतिभागियों को KalaManch.art पर रजिस्टर करना होगा।
2. Artist Profile बनाना जरूरी है।
3. अधिकतम 3 अलग-अलग आर्टवर्क जमा किए जा सकते हैं।
4. जमा की गई कला पूरी तरह मौलिक होनी चाहिए।
5. Fully AI-generated artwork मान्य नहीं है।
6. शॉर्टलिस्टेड कलाकारों से creation proof मांगा जा सकता है।
7. कला का स्वामित्व कलाकार के पास रहेगा।
8. KalaManch केवल प्रतियोगिता, परिणाम, प्रमाणपत्र और कलाकार प्रमोशन हेतु कला दिखा सकता है, कलाकार को क्रेडिट के साथ।
9. बिना अनुमति कला की कॉपी, ट्रेस, रीपोस्ट, बिक्री या दुरुपयोग प्रतिबंधित है।
10. ज्यूरी और KalaManch का निर्णय अंतिम होगा।

सबमिशन गाइडलाइन:
- High-quality artwork image
- Artwork title
- Artwork medium
- Optional artwork story/description

शॉर्टलिस्टेड वेरिफिकेशन:
- Progress images
- 10-30 सेकंड creation video
- Brushwork/sketching clip
- Timelapse video
- Digital painting screenshots
- Screen recording

Nominate 5 Artists Challenge:
भाग लेने के बाद कलाकार 5 अन्य कलाकारों को nominate कर सकते हैं और उन्हें KalaManch.art से जुड़ने के लिए आमंत्रित कर सकते हैं।

KalaManch Android App अब LIVE है:
https://play.google.com/store/apps/details?id=art.kalamanch.app

भागीदारी FREE है। हर टैलेंट को मंच।`;

const foundingPaintersHindiPrize = `प्रतियोगिता संरचना:
- Top 25 Artists: Officially Shortlisted
- Top 10 Artists: Finalists Showcase
- Top 3 Artists: Grand Winners
- Special Jury Mentions भी दिए जा सकते हैं।

विजेताओं को मिल सकता है:
- Art Kits
- Digital Winner Certificates
- KalaManch.art पर Featured Placement
- KalaManch Android App पर Priority Showcase
- Founding Artist Recognition
- Social media promotion
- Future KalaManch campaigns में अवसर

सभी प्रतिभागियों को मिल सकता है:
- Digital Participation Certificate
- Founding Artist Badge
- Community Recognition
- Website और app feature opportunity`;

const emptySubmissionForm = {
  artwork_title: '',
  medium: '',
  category: '',
  description: '',
  artwork_image_url: '',
  process_video_url: '',
  progress_image_urls: '',
  instagram_url: '',
  referral_handles: '',
};

const listFromContent = (value, fallback) => {
  const items = String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length ? items : fallback;
};

const tiersFromContent = (value, fallback) => {
  const tiers = String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [count, ...labelParts] = item.split('|').map((part) => part.trim());
      return [count, labelParts.join(' | ') || 'Recognition'];
    })
    .filter(([count]) => count);
  return tiers.length ? tiers : fallback;
};

const generalCompetitionGuidelines = {
  en: {
    eyebrow: 'General Guidelines',
    title: 'Competition rules for every creative form',
    intro: 'These guidelines apply to KalaManch competitions, challenges, contests, and creative events across painting, singing, dance, poetry, theatre, pottery, clay work, folk arts, digital work, and future creative formats.',
    note: 'Participants should read the competition-specific page as well, because each live competition may have its own categories, dates, proof requirements, rewards, and submission limits.',
    sections: [
      {
        title: 'Eligibility',
        items: [
          ['Open across India', 'Artists from any Indian city, town, or region can participate unless a competition says otherwise.'],
          ['Artist profile required', 'Participants must register and create a valid KalaManch artist profile.'],
          ['Multiple competitions', 'Artists may join more than one competition unless a specific event restricts it.'],
        ],
      },
      {
        title: 'Submission Rules',
        items: [
          ['Original work only', 'Entries must be created by the participant and submitted in the right competition category.'],
          ['Limits may vary', 'Entry count, file type, proof, and deadline rules are set separately for each competition.'],
          ['Late entries', 'Entries submitted after the stated deadline may not be accepted.'],
        ],
      },
      {
        title: 'Prohibited Content',
        items: [
          ['No copied work', 'Copied, traced, stolen, plagiarized, or falsely represented entries are not allowed.'],
          ['No unsafe content', 'Offensive, hateful, violent, adult, illegal, or misleading submissions may be rejected.'],
          ['AI disclosure', 'AI-generated work cannot be submitted as fully handmade/original unless the competition specifically allows AI categories.'],
        ],
      },
      {
        title: 'Verification & Authenticity',
        items: [
          ['Shortlisted proof', 'Selected participants may be asked for progress photos, videos, raw files, timelapse, screenshots, or process clips.'],
          ['Manual review', 'KalaManch and jury members may manually review suspicious submissions before final selection.'],
        ],
      },
      {
        title: 'Judging & Results',
        items: [
          ['Review process', 'Entries may be reviewed by jury members, moderators, and in some cases audience engagement.'],
          ['Selection criteria', 'Creativity, originality, presentation, effort, and category relevance may be considered.'],
          ['Final decision', 'Jury and KalaManch decisions will be final.'],
        ],
      },
      {
        title: 'Usage & Promotion Rights',
        items: [
          ['Artist ownership', 'Artists retain ownership of submitted work. KalaManch will not sell or transfer ownership without permission.'],
          ['Promotional display', 'Selected entries may be displayed for competition-related promotion with artist credit whenever possible.'],
        ],
      },
      {
        title: 'Community Conduct',
        items: [
          ['Respect artists', 'Support fellow artists and avoid harassment, bullying, hate speech, spam, or fake engagement.'],
          ['Protect work', 'Do not misuse, repost, sell, or copy another artist’s submission without permission.'],
        ],
      },
      {
        title: 'Certificates & Rewards',
        items: [
          ['Recognition varies', 'Digital certificates, featured placement, and rewards may vary by competition.'],
          ['Public showcase', 'Approved entries may appear publicly on competition pages and selected KalaManch platforms.'],
        ],
      },
    ],
    closingTitle: 'Fair creativity matters',
    closingBody: 'KalaManch.art is built to support genuine artists, creators, and creative communities across India. We encourage originality, respectful participation, and real creative effort.',
  },
  hi: {
    eyebrow: 'सामान्य दिशानिर्देश',
    title: 'हर रचनात्मक प्रतियोगिता के लिए नियम',
    intro: 'ये दिशानिर्देश KalaManch.art पर आयोजित पेंटिंग, गायन, नृत्य, कविता, थिएटर, पॉटरी, क्ले वर्क, लोक कला, डिजिटल कार्य और भविष्य के सभी रचनात्मक प्रतियोगिताओं पर लागू होंगे।',
    note: 'हर लाइव प्रतियोगिता का अपना अलग पेज भी पढ़ें, क्योंकि श्रेणियां, तारीख, प्रूफ, पुरस्कार और सबमिशन सीमा प्रतियोगिता के अनुसार अलग हो सकती है।',
    sections: [
      {
        title: 'पात्रता',
        items: [
          ['भारत भर के कलाकार', 'जब तक अलग से न बताया जाए, भारत के किसी भी शहर, कस्बे या क्षेत्र के कलाकार भाग ले सकते हैं।'],
          ['Artist profile आवश्यक', 'प्रतिभागियों को KalaManch पर रजिस्टर करके वैध कलाकार प्रोफाइल बनानी होगी।'],
          ['कई प्रतियोगिताएं', 'कलाकार एक से अधिक प्रतियोगिताओं में भाग ले सकते हैं, जब तक कोई विशेष प्रतिबंध न हो।'],
        ],
      },
      {
        title: 'सबमिशन नियम',
        items: [
          ['केवल मौलिक कार्य', 'एंट्री प्रतिभागी द्वारा बनाई गई होनी चाहिए और सही श्रेणी में जमा होनी चाहिए।'],
          ['सीमा अलग हो सकती है', 'एंट्री संख्या, फाइल प्रकार, प्रूफ और अंतिम तिथि हर प्रतियोगिता के अनुसार तय होगी।'],
          ['देरी से सबमिशन', 'अंतिम तिथि के बाद जमा एंट्री स्वीकार नहीं की जा सकती।'],
        ],
      },
      {
        title: 'प्रतिबंधित सामग्री',
        items: [
          ['कॉपी किया हुआ कार्य नहीं', 'कॉपी, ट्रेस, चोरी, plagiarized या गलत तरीके से प्रस्तुत एंट्री मान्य नहीं होगी।'],
          ['असुरक्षित सामग्री नहीं', 'आपत्तिजनक, घृणास्पद, हिंसात्मक, अश्लील, गैरकानूनी या भ्रामक सबमिशन रिजेक्ट हो सकता है।'],
          ['AI disclosure', 'AI-generated work को पूरी तरह handmade/original बताकर जमा नहीं किया जा सकता, जब तक प्रतियोगिता AI category की अनुमति न दे।'],
        ],
      },
      {
        title: 'सत्यापन और प्रामाणिकता',
        items: [
          ['Shortlisted proof', 'चयनित प्रतिभागियों से progress photos, videos, raw files, timelapse, screenshots या process clips मांगे जा सकते हैं।'],
          ['Manual review', 'KalaManch और ज्यूरी संदिग्ध एंट्री की मैन्युअल जांच कर सकते हैं।'],
        ],
      },
      {
        title: 'जजिंग और परिणाम',
        items: [
          ['Review process', 'एंट्री ज्यूरी, moderators और कुछ प्रतियोगिताओं में audience engagement से review हो सकती है।'],
          ['Selection criteria', 'Creativity, originality, presentation, effort और category relevance देखे जा सकते हैं।'],
          ['Final decision', 'ज्यूरी और KalaManch का निर्णय अंतिम होगा।'],
        ],
      },
      {
        title: 'उपयोग और प्रमोशन अधिकार',
        items: [
          ['Artist ownership', 'कलाकार अपने submitted work के स्वामी रहेंगे। KalaManch बिना अनुमति ownership नहीं बेचेगा या transfer करेगा।'],
          ['Promotional display', 'चयनित एंट्री competition-related promotion में artist credit के साथ दिखाई जा सकती है।'],
        ],
      },
      {
        title: 'समुदाय व्यवहार',
        items: [
          ['कलाकारों का सम्मान', 'अन्य कलाकारों का समर्थन करें और harassment, bullying, hate speech, spam या fake engagement से बचें।'],
          ['कार्य की सुरक्षा', 'बिना अनुमति किसी कलाकार की entry को copy, repost, sell या misuse न करें।'],
        ],
      },
      {
        title: 'प्रमाणपत्र और पुरस्कार',
        items: [
          ['Recognition अलग हो सकता है', 'Digital certificates, featured placement और rewards प्रतियोगिता के अनुसार अलग हो सकते हैं।'],
          ['Public showcase', 'Approved entries competition pages और selected KalaManch platforms पर दिखाई जा सकती हैं।'],
        ],
      },
    ],
    closingTitle: 'निष्पक्ष रचनात्मकता महत्वपूर्ण है',
    closingBody: 'KalaManch.art भारत के वास्तविक कलाकारों, creators और creative communities को support करने के लिए बनाया गया है। हम originality, respectful participation और real creative effort को प्रोत्साहित करते हैं।',
  },
};

const Competitions = () => {
  const navigate = useNavigate();
  const content = usePageContent('competitions');
  const { isAuthenticated: artistLoggedIn } = useArtistAuth();
  const [activeTab, setActiveTab] = useState('live');
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionCompetition, setSubmissionCompetition] = useState(null);
  const [submissionForm, setSubmissionForm] = useState(emptySubmissionForm);
  const [submissionBusy, setSubmissionBusy] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [detailCompetition, setDetailCompetition] = useState(null);
  const [detailLanguage, setDetailLanguage] = useState('en');
  const [guidelinesLanguage, setGuidelinesLanguage] = useState('en');

  useEffect(() => {
    let mounted = true;
    const loadCompetitions = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/competitions`);
        if (!response.ok) throw new Error('Unable to load competitions');
        const data = await response.json();
        if (mounted) setCompetitions(data.competitions || []);
      } catch (error) {
        if (mounted) setCompetitions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCompetitions();
    return () => {
      mounted = false;
    };
  }, []);

  const grouped = useMemo(() => {
    return tabs.reduce((acc, tab) => {
      acc[tab.id] = competitions.filter((competition) => (competition.status || 'upcoming') === tab.id);
      return acc;
    }, {});
  }, [competitions]);

  const visibleCompetitions = grouped[activeTab] || [];
  const liveCompetitions = grouped.live || [];
  const hasAnyCompetition = competitions.length > 0;
  const bannerActive = String(content.banner_active || 'yes').toLowerCase() !== 'no';
  const hasBanner = bannerActive && (content.banner_title || content.banner_subtitle || content.banner_image_url);
  const managedAllowedCategories = listFromContent(content.allowed_categories, artCategories);
  const managedNotAllowedItems = listFromContent(content.not_allowed_items, notAllowedItems);
  const managedProofItems = listFromContent(content.proof_items, proofItems);
  const managedRecognitionTiers = tiersFromContent(content.recognition_tiers, recognitionTiers);
  const managedSocialCaption = content.social_caption || defaultSocialCaption;
  const isFoundingPaintersCompetition = (competition) => /founding painters challenge 2026/i.test(competition?.name || '');
  const competitionPoster = (competition) => competition?.poster_url || (isFoundingPaintersCompetition(competition) ? foundingPaintersPoster : '') || competition?.image_url || '';
  const competitionHindiDescription = (competition) => competition?.description_hi || (isFoundingPaintersCompetition(competition) ? foundingPaintersHindiDetails : '');
  const competitionHindiPrize = (competition) => competition?.prize_details_hi || (isFoundingPaintersCompetition(competition) ? foundingPaintersHindiPrize : '');
  const guidelines = generalCompetitionGuidelines[guidelinesLanguage];

  useEffect(() => {
    if (loading || window.location.hash !== '#live-competitions') return;
    const timer = window.setTimeout(() => {
      document.getElementById('live-competitions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [loading, liveCompetitions.length]);

  const reloadCompetitions = async () => {
    const response = await fetch(`${API}/competitions`);
    if (!response.ok) throw new Error('Unable to load competitions');
    const data = await response.json();
    setCompetitions(data.competitions || []);
  };

  const openSubmissionForm = (competition) => {
    setDetailCompetition(null);
    setSubmissionCompetition(competition);
    setSubmissionForm(emptySubmissionForm);
    setSubmissionStatus('');
    setSubmissionError('');
    window.setTimeout(() => {
      document.getElementById('competition-submit-entry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const openCompetitionDetails = (competition, language = 'en') => {
    navigate(`/competitions/${competition.id}${language === 'hi' ? '?lang=hi' : ''}`);
  };

  const openRegistration = (competition) => {
    if (artistLoggedIn) {
      navigate(`/competitions/${competition.id}#submit-artwork`);
      return;
    }
    setDetailCompetition(null);
    window.dispatchEvent(new Event('openArtistJoin'));
  };

  const updateSubmissionField = (field, value) => {
    setSubmissionForm((current) => ({ ...current, [field]: value }));
  };

  const uploadArtworkImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingArtwork(true);
    setSubmissionError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/project-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      updateSubmissionField('artwork_image_url', response.data.image_url || '');
    } catch (error) {
      setSubmissionError(error.response?.data?.detail || 'Unable to upload artwork image.');
    } finally {
      setUploadingArtwork(false);
      event.target.value = '';
    }
  };

  const submitArtworkEntry = async (event) => {
    event.preventDefault();
    if (!submissionCompetition) return;
    if (!artistLoggedIn) {
      setSubmissionError('Login as an artist before submitting artwork.');
      return;
    }
    setSubmissionBusy(true);
    setSubmissionError('');
    setSubmissionStatus('');
    try {
      const payload = {
        ...submissionForm,
        progress_image_urls: submissionForm.progress_image_urls
          .split(/\r?\n/)
          .map((item) => item.trim())
          .filter(Boolean),
        referral_handles: submissionForm.referral_handles
          .split(/\r?\n|,/)
          .map((item) => item.trim())
          .filter(Boolean),
      };
      await axios.post(`${API}/artist-auth/competitions/${submissionCompetition.id}/submissions`, payload, { withCredentials: true });
      setSubmissionStatus('Artwork submitted. Admin will review it before it appears publicly.');
      setSubmissionForm(emptySubmissionForm);
      await reloadCompetitions();
    } catch (error) {
      setSubmissionError(error.response?.data?.detail || 'Unable to submit artwork right now.');
    } finally {
      setSubmissionBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-[#F8FAFC] pt-28 text-slate-950">
      <SEO
        path="/competitions"
        title="KalaManch Competitions"
        description="Discover live, upcoming, and past KalaManch competitions for artists, performers, creators, and cultural communities."
        keywords="KalaManch competitions, artist competitions India, online art competitions, performance contests"
      />

      <section className="order-1 border-b border-orange-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <p className="text-sm font-extrabold uppercase tracking-[0.35em] text-[#FF6F00]">Competitions</p>
          <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-end">
            <div>
              <h1 className="max-w-4xl text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
                {content.hero_title || 'KalaManch Competitions'}
              </h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-700">
                {content.hero_subtitle || 'KalaManch will organise online and hybrid creative competitions for Indian artists across painting, music, dance, poetry, theatre, folk arts, digital art, and regional creative forms.'}
              </p>
            </div>
            <div className="rounded-[2rem] border border-orange-100 bg-orange-50/70 p-6 shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#7C2D12]">How it works</p>
              <p className="mt-3 text-base leading-7 text-slate-700">
                {content.body || 'Artists submit entries, admin reviews them, approved work appears in the competition showcase, and shortlisted/final winners are published on the competition page.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="order-2 mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            ['Discover talent', 'Competitions help new and experienced artists get visibility beyond their city.'],
            ['Reviewed showcase', 'Submissions are checked by admin before public display, keeping the page authentic and safe.'],
            ['Winners and recognition', 'Each competition can show approved entries, shortlisted artists, finalists, winners, sponsors, and certificates.'],
          ].map(([title, body]) => (
            <article key={title} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">{title}</p>
              <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="order-6 mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl">
          <div className="grid gap-0 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="bg-slate-950 p-7 text-white sm:p-8 lg:p-10">
              <p className="text-sm font-black uppercase tracking-[0.3em] text-orange-200">{guidelines.eyebrow}</p>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl">{guidelines.title}</h2>
              <p className="mt-5 text-base font-semibold leading-7 text-white/82">{guidelines.intro}</p>
              <p className="mt-5 rounded-3xl border border-white/12 bg-white/8 p-4 text-sm leading-6 text-white/75">
                {guidelines.note}
              </p>
              <div className="mt-6 inline-flex rounded-full border border-white/15 bg-white/10 p-1">
                <button
                  type="button"
                  onClick={() => setGuidelinesLanguage('en')}
                  className={`rounded-full px-4 py-2 text-sm font-black ${guidelinesLanguage === 'en' ? 'bg-white text-slate-950' : 'text-white/75'}`}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setGuidelinesLanguage('hi')}
                  className={`rounded-full px-4 py-2 text-sm font-black ${guidelinesLanguage === 'hi' ? 'bg-white text-slate-950' : 'text-white/75'}`}
                >
                  हिंदी
                </button>
              </div>
            </div>

            <div className="p-5 sm:p-6 lg:p-8">
              <div className="grid gap-4 md:grid-cols-2">
                {guidelines.sections.map((section) => (
                  <article key={section.title} className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-5">
                    <h3 className="text-lg font-black text-slate-950">{section.title}</h3>
                    <div className="mt-4 space-y-3">
                      {section.items.map(([label, body]) => (
                        <div key={label} className="rounded-2xl bg-white p-4 shadow-sm">
                          <p className="text-sm font-black text-[#4A148C]">{label}</p>
                          <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">{body}</p>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
              <div className="mt-5 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-5">
                <p className="text-xl font-black text-emerald-950">{guidelines.closingTitle}</p>
                <p className="mt-2 text-sm font-semibold leading-6 text-emerald-900">{guidelines.closingBody}</p>
                <p className="mt-3 text-sm font-black text-[#FF6F00]">Har Talent Ko Manch</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {liveCompetitions.length > 0 && (
        <section id="live-competitions" className="order-3 mx-auto w-full max-w-7xl scroll-mt-28 px-4 pt-8 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] border border-emerald-200 bg-white shadow-xl">
            <div className="grid gap-0 lg:grid-cols-[0.75fr_1.25fr]">
              <div className="bg-gradient-to-br from-emerald-700 via-[#0F766E] to-[#FF6F00] p-7 text-white sm:p-8">
                <p className="text-sm font-black uppercase tracking-[0.28em] text-emerald-100">Live now</p>
                <h2 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">Live competitions are open</h2>
                <p className="mt-4 text-base font-semibold leading-7 text-white/86">
                  Artists can view details, register, and submit artwork directly from the live competition cards.
                </p>
              </div>
              <div className="grid gap-4 p-5 sm:p-6 lg:grid-cols-2">
                {liveCompetitions.map((competition) => (
                  <article key={competition.id} className="rounded-[1.5rem] border border-emerald-100 bg-emerald-50/60 p-5">
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-emerald-700 ring-1 ring-emerald-200">
                        Live
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                        {modeLabels[competition.mode] || 'Online'}
                      </span>
                    </div>
                    <h3 className="mt-4 text-2xl font-black leading-tight text-slate-950">{competition.name}</h3>
                    <div className="mt-3 space-y-1 text-sm font-semibold text-slate-600">
                      {competition.competition_date && <p>Date: {competition.competition_date}</p>}
                      {(competition.city || competition.venue) && <p>{[competition.venue, competition.city].filter(Boolean).join(', ')}</p>}
                    </div>
                    {competition.description && (
                      <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-700">{competition.description}</p>
                    )}
                    <div className="mt-5 flex flex-wrap gap-3">
                      <Link
                        to={`/competitions/${competition.id}`}
                        className="rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
                      >
                        View Details
                      </Link>
                      <button
                        type="button"
                        onClick={() => openRegistration(competition)}
                        className="rounded-full border border-[#FF6F00] bg-white px-5 py-3 text-sm font-extrabold text-[#C45A00] transition hover:-translate-y-0.5 hover:bg-orange-50"
                      >
                        {artistLoggedIn ? 'Submit Artwork' : 'Register / Login'}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {hasBanner && (
        <section className="order-5 mx-auto w-full max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl lg:grid lg:grid-cols-[1fr_0.9fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-sm font-extrabold uppercase tracking-[0.3em] text-[#FF6F00]">Competition Highlight</p>
              <h2 className="mt-4 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
                {content.banner_title || 'KalaManch competition banner'}
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-700">
                {content.banner_subtitle || 'Admin can use this section to promote the most important current or upcoming competition.'}
              </p>
              {content.banner_link_url && (
                <a
                  href={content.banner_link_url}
                  className="mt-6 inline-flex rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
                >
                  View Details
                </a>
              )}
            </div>
            {content.banner_image_url && (
              <img src={content.banner_image_url} alt={content.banner_title || 'Competition banner'} className="h-64 w-full object-cover lg:h-full" />
            )}
          </div>
        </section>
      )}

      <section className="order-4 mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full px-5 py-3 text-sm font-extrabold transition ${
                activeTab === tab.id
                  ? 'bg-[#FF6F00] text-white shadow-lg shadow-orange-200'
                  : 'border border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:text-[#C45A00]'
              }`}
            >
              {tab.label}
              <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5">{grouped[tab.id]?.length || 0}</span>
            </button>
          ))}
        </div>

        {submissionCompetition && (
          <form
            id="competition-submit-entry"
            onSubmit={submitArtworkEntry}
            className="mt-8 rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-xl sm:p-8"
          >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.28em] text-[#00A78E]">Submit artwork</p>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{submissionCompetition.name}</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Your entry will stay private until Kalamanch admin reviews and approves it. Approved artwork appears publicly on this competition page.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSubmissionCompetition(null)}
                className="rounded-full border border-slate-200 px-5 py-2 text-sm font-extrabold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            {!artistLoggedIn && (
              <div className="mt-5 rounded-3xl border border-orange-200 bg-orange-50 p-5 text-sm font-bold text-[#7C2D12]">
                Login as an artist to submit artwork. Competition browsing remains open to everyone.
              </div>
            )}

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <input
                value={submissionForm.artwork_title}
                onChange={(event) => updateSubmissionField('artwork_title', event.target.value)}
                placeholder="Artwork title"
                className="rounded-2xl border border-slate-300 px-4 py-3"
                required
              />
              <input
                value={submissionForm.medium}
                onChange={(event) => updateSubmissionField('medium', event.target.value)}
                placeholder="Medium, e.g. watercolor, acrylic, digital painting"
                className="rounded-2xl border border-slate-300 px-4 py-3"
                required
              />
              <select
                value={submissionForm.category}
                onChange={(event) => updateSubmissionField('category', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3"
              >
                <option value="">Select category</option>
                {managedAllowedCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <input
                value={submissionForm.instagram_url}
                onChange={(event) => updateSubmissionField('instagram_url', event.target.value)}
                placeholder="Instagram post URL (optional)"
                className="rounded-2xl border border-slate-300 px-4 py-3"
              />
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <label className="block text-sm font-black text-slate-900">Artwork image</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={uploadArtworkImage}
                className="mt-2 w-full text-sm text-slate-700"
                disabled={!artistLoggedIn || uploadingArtwork}
              />
              <p className="mt-2 text-xs text-slate-500">Upload a clear JPG, PNG, or WebP artwork image. User uploads are safety scanned when scanning is configured.</p>
              {uploadingArtwork && <p className="mt-2 text-sm font-bold text-[#FF6F00]">Uploading artwork...</p>}
              <input
                value={submissionForm.artwork_image_url}
                onChange={(event) => updateSubmissionField('artwork_image_url', event.target.value)}
                placeholder="Artwork image URL appears here after upload"
                className="mt-3 w-full rounded-2xl border border-slate-300 px-4 py-3"
                required
              />
            </div>

            <textarea
              value={submissionForm.description}
              onChange={(event) => updateSubmissionField('description', event.target.value)}
              placeholder="Short artwork description or story behind the artwork"
              className="mt-4 min-h-[110px] w-full rounded-2xl border border-slate-300 px-4 py-3"
            />
            <textarea
              value={submissionForm.progress_image_urls}
              onChange={(event) => updateSubmissionField('progress_image_urls', event.target.value)}
              placeholder="Optional progress image URLs, one per line. Shortlisted artists may be asked for proof later."
              className="mt-4 min-h-[90px] w-full rounded-2xl border border-slate-300 px-4 py-3"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <input
                value={submissionForm.process_video_url}
                onChange={(event) => updateSubmissionField('process_video_url', event.target.value)}
                placeholder="Optional process video / timelapse URL"
                className="rounded-2xl border border-slate-300 px-4 py-3"
              />
              <input
                value={submissionForm.referral_handles}
                onChange={(event) => updateSubmissionField('referral_handles', event.target.value)}
                placeholder="Optional artist tags/referrals, comma separated"
                className="rounded-2xl border border-slate-300 px-4 py-3"
              />
            </div>

            <div className="mt-5 rounded-3xl bg-blue-50 p-4 text-sm leading-6 text-blue-950">
              Artwork remains the artist's property. Kalamanch may show approved entries only for competition display,
              results, certificates, and artist promotion with credit. Copying another artist's work is not allowed.
            </div>

            {submissionError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{submissionError}</p>}
            {submissionStatus && <p className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">{submissionStatus}</p>}

            <button
              type="submit"
              disabled={submissionBusy || !artistLoggedIn}
              className="mt-5 rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {submissionBusy ? 'Submitting...' : 'Submit for Admin Review'}
            </button>
          </form>
        )}

        {loading ? (
          <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">
            Loading competitions...
          </div>
        ) : visibleCompetitions.length === 0 ? (
          <div className="mt-8 rounded-[2rem] border border-dashed border-orange-200 bg-white p-8 text-center shadow-sm sm:p-12">
            <p className="text-sm font-extrabold uppercase tracking-[0.32em] text-[#FF6F00]">
              {hasAnyCompetition ? tabs.find((tab) => tab.id === activeTab)?.label : 'Coming Soon'}
            </p>
            <h2 className="mt-4 text-3xl font-black text-slate-950">Competitions coming soon.</h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              New Kalamanch competitions will appear here as soon as the admin team publishes them.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visibleCompetitions.map((competition) => (
              <article
                key={competition.id}
                role="button"
                tabIndex={0}
                onClick={() => openCompetitionDetails(competition)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openCompetitionDetails(competition);
                  }
                }}
                className="cursor-pointer overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-100"
              >
                {competitionPoster(competition) ? (
                  <img src={competitionPoster(competition)} alt={competition.name} className="h-48 w-full object-cover" />
                ) : (
                  <div className="flex h-40 items-center justify-center bg-gradient-to-br from-[#FFF7ED] via-white to-[#ECFDF5]">
                    <span className="text-5xl font-black text-[#FF6F00]">{competition.name?.charAt(0) || 'C'}</span>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ring-1 ${statusStyles[competition.status] || statusStyles.upcoming}`}>
                      {competition.status || 'upcoming'}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                      {modeLabels[competition.mode] || 'Online'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCompetitionDetails(competition);
                    }}
                    className="mt-4 block text-left"
                  >
                    <h2 className="text-2xl font-black leading-tight text-slate-950 hover:text-[#FF6F00]">{competition.name}</h2>
                  </button>
                  <div className="mt-3 space-y-1 text-sm font-semibold text-slate-600">
                    {competition.competition_date && <p>Date: {competition.competition_date}</p>}
                    {(competition.city || competition.venue) && <p>{[competition.venue, competition.city].filter(Boolean).join(', ')}</p>}
                  </div>
                  {competition.description && (
                    <p className="mt-4 line-clamp-4 text-sm leading-6 text-slate-700">
                      {competition.description}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCompetitionDetails(competition);
                    }}
                    className="mt-3 text-sm font-black text-[#FF6F00] hover:text-[#C45A00]"
                  >
                    View full details
                  </button>
                  {competition.prize_details && (
                    <p className="mt-4 line-clamp-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">{competition.prize_details}</p>
                  )}
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/competitions/${competition.id}#submit-artwork`);
                      }}
                      className="inline-flex rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
                    >
                      Open Registration
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/competitions/${competition.id}#submit-artwork`);
                      }}
                      className="inline-flex rounded-full border border-[#FF6F00] px-5 py-3 text-sm font-extrabold text-[#C45A00] transition hover:-translate-y-0.5 hover:bg-orange-50"
                    >
                      Submit Artwork
                    </button>
                  </div>
                  {competition.approved_submissions?.length > 0 && (
                    <div className="mt-6 border-t border-slate-100 pt-5">
                      <p className="text-xs font-black uppercase tracking-[0.22em] text-[#00A78E]">Approved showcase</p>
                      <div className="mt-3 grid gap-3">
                        {competition.approved_submissions.slice(0, 4).map((submission) => (
                          <div key={submission.id} className="grid gap-3 rounded-2xl bg-slate-50 p-3 sm:grid-cols-[80px_1fr]">
                            <img src={submission.artwork_image_url} alt={submission.artwork_title} className="h-20 w-full rounded-xl object-cover" />
                            <div>
                              <p className="font-black text-slate-950">{submission.artwork_title}</p>
                              <p className="text-xs font-bold text-slate-600">{submission.artist_name} • {submission.medium}</p>
                              {submission.description && <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">{submission.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {detailCompetition && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 px-4 py-8 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="relative w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => setDetailCompetition(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-white/95 px-4 py-2 text-sm font-black text-slate-800 shadow-lg hover:bg-orange-50"
            >
              Close
            </button>
            {competitionPoster(detailCompetition) ? (
              <div className="bg-slate-950 p-4 sm:p-6">
                <img
                  src={competitionPoster(detailCompetition)}
                  alt={detailCompetition.name}
                  className="mx-auto max-h-[78vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
                />
              </div>
            ) : (
              <div className="flex h-52 items-center justify-center bg-gradient-to-br from-[#FFF7ED] via-white to-[#ECFDF5]">
                <span className="text-7xl font-black text-[#FF6F00]">{detailCompetition.name?.charAt(0) || 'C'}</span>
              </div>
            )}
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ring-1 ${statusStyles[detailCompetition.status] || statusStyles.upcoming}`}>
                  {detailCompetition.status || 'upcoming'}
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                  {modeLabels[detailCompetition.mode] || 'Online'}
                </span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDetailLanguage('en')}
                  className={`rounded-full px-4 py-2 text-sm font-black ${detailLanguage === 'en' ? 'bg-[#FF6F00] text-white' : 'border border-orange-200 text-[#C45A00]'}`}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setDetailLanguage('hi')}
                  disabled={!competitionHindiDescription(detailCompetition) && !competitionHindiPrize(detailCompetition)}
                  className={`rounded-full px-4 py-2 text-sm font-black ${detailLanguage === 'hi' ? 'bg-[#4A148C] text-white' : 'border border-purple-200 text-[#4A148C]'} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  हिंदी
                </button>
              </div>
              <h2 className="mt-4 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">{detailCompetition.name}</h2>
              <div className="mt-4 grid gap-2 text-sm font-semibold text-slate-600 sm:grid-cols-2">
                {detailCompetition.competition_date && <p>Date: {detailCompetition.competition_date}</p>}
                {(detailCompetition.city || detailCompetition.venue) && <p>{[detailCompetition.venue, detailCompetition.city].filter(Boolean).join(', ')}</p>}
              </div>
              {(detailLanguage === 'hi' ? (competitionHindiDescription(detailCompetition) || detailCompetition.description) : detailCompetition.description) && (
                <div className="mt-6 whitespace-pre-wrap rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm leading-7 text-slate-700">
                  {detailLanguage === 'hi' ? (competitionHindiDescription(detailCompetition) || detailCompetition.description) : detailCompetition.description}
                </div>
              )}
              {(detailLanguage === 'hi' ? (competitionHindiPrize(detailCompetition) || detailCompetition.prize_details) : detailCompetition.prize_details) && (
                <div className="mt-5 whitespace-pre-wrap rounded-3xl bg-emerald-50 p-5 text-sm font-semibold leading-7 text-emerald-800">
                  {detailLanguage === 'hi' ? (competitionHindiPrize(detailCompetition) || detailCompetition.prize_details) : detailCompetition.prize_details}
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openRegistration(detailCompetition)}
                  className="rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
                >
                  {artistLoggedIn ? 'Open Submission Form' : 'Artist Login / Registration'}
                </button>
                <button
                  type="button"
                  onClick={() => openSubmissionForm(detailCompetition)}
                  className="rounded-full border border-[#FF6F00] px-6 py-3 text-sm font-extrabold text-[#C45A00] transition hover:-translate-y-0.5 hover:bg-orange-50"
                >
                  Submit Artwork
                </button>
              </div>

              <div className="mt-8 border-t border-slate-200 pt-6">
                <p className="text-sm font-black uppercase tracking-[0.24em] text-[#00A78E]">Approved artwork showcase</p>
                {detailCompetition.approved_submissions?.length > 0 ? (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {detailCompetition.approved_submissions.map((submission) => (
                      <article key={submission.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                        <img src={submission.artwork_image_url} alt={submission.artwork_title} className="h-44 w-full object-cover" />
                        <div className="p-4">
                          <p className="font-black text-slate-950">{submission.artwork_title}</p>
                          <p className="mt-1 text-xs font-bold text-slate-600">{submission.artist_name} | {submission.medium}</p>
                          {submission.description && <p className="mt-2 text-sm leading-6 text-slate-600">{submission.description}</p>}
                          {submission.process_video_url && (
                            <a href={submission.process_video_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex text-sm font-black text-[#FF6F00]">
                              View process video
                            </a>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-3xl border border-dashed border-emerald-200 bg-emerald-50/40 p-6 text-sm font-semibold text-emerald-900">
                    Approved submissions and winners for this competition will appear here after admin review.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Competitions;
